import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

// UI state for the plans/subscription surface: modal/drawer visibility flags
// that other slices don't care about. Kept separate from plans-preferences
// and plans-data so toggling a modal doesn't invalidate selectors of
// unrelated business state.

// Which surface owns the checkout render while `showModalCheckout` is true.
// The checkout UI (`CheckoutV2`) is rendered by several surfaces that all gate
// on the same `showModalCheckout` flag: the app-wide `GlobalCheckoutModal`
// (mounted in SessionShell, present on every authenticated route incl.
// onboarding) and the onboarding-only surfaces (OnboardingCheckoutDrawer,
// AnnualBanner, RecommendedPlanDrawer, v1 intro). This discriminator decides
// which one renders so they never stack into a double checkout — and so a
// `?add_to_cart` deeplink can open the global checkout over onboarding without
// the onboarding surfaces also reacting to the shared flag.
export type CheckoutHost = 'global' | 'onboarding';

/**
 * Desde dónde se abrió la pantalla de compra exitosa (y el recorrido que sale de
 * ella). Viaja al `page_view` de esa pantalla y a los eventos del tour porque
 * `is_reentry` no alcanza: colapsa todas las reentradas en un solo `true`, y sin
 * separarlas no se puede saber qué superficie trae de vuelta a la gente.
 *
 * Los seis primeros abren el recorrido; los dos últimos solo pintan el
 * comprobante (el detalle de una compra en Pagos, y el aterrizaje del redirect
 * externo de 3DS/PayPal, que no pasa por ninguna superficie de la app).
 */
export type PurchaseSuccessOrigin =
	| 'checkout'
	| 'milestone'
	| 'plans'
	| 'quick_action'
	/** La tarjeta de entrega del home (features/delivery-cycle): superficie
	 * propia, no la lista de "Primeros pasos". */
	| 'delivery_cycle'
	| 'subscriptions'
	| 'purchase_detail'
	| 'checkout_redirect'
	/**
	 * El preview de diseño (`?preview_purchase=`). Origen PROPIO y no el de la
	 * superficie que imita: si reusara `quick_action`, cada vez que alguien
	 * diseña la pantalla se sumaría un `purchase_tour_started` falso al número
	 * real de esa superficie. Solo se emite con VITE_PURCHASE_PREVIEW.
	 */
	| 'preview';

export interface PlansUIState {
	showModalCheckout: boolean;
	/**
	 * El tour post-compra, ya fuera del checkout. Vivía como `successSlot`
	 * DENTRO de `CheckoutV2`, y ahí heredaba un chrome que no es el suyo: el
	 * recorrido trae su propio header, su barra pegada abajo y sus
	 * ilustraciones a sangre, y encimados al modal del checkout se rompían.
	 *
	 * Es un flag aparte y no una etapa de `showModalCheckout` porque tiene que
	 * SOBREVIVIR al cierre del checkout: primero se cierra el checkout, después
	 * se abre el tour.
	 */
	showPurchaseTour: boolean;
	/**
	 * Qué compra abre el tour. `null` = la recién hecha, que sale de storage:
	 * pedirla por carrito la marcaría como reentrada y se saltaría el evento de
	 * compra y el vaciado del carrito.
	 */
	purchaseTourCartId: number | null;
	/** Superficie que abrió el tour. Solo alimenta tracking. */
	purchaseTourOrigin: PurchaseSuccessOrigin;
	checkoutHost: CheckoutHost;
	/**
	 * The cart the checkout is building (see shared/checkout/pendingCart).
	 * While not `null` the checkout shows its loading screen and does not fetch
	 * the order on its own. It survives closing on purpose: if the user closes
	 * mid-build, the build continues and isn't repeated on reopen.
	 */
	pendingCartId: number | null;
	showModalCancelPendingOrder: boolean;
	showRecommendedPlanDrawer: boolean;
}

const initialState: PlansUIState = {
	showModalCheckout: false,
	showPurchaseTour: false,
	purchaseTourCartId: null,
	purchaseTourOrigin: 'checkout',
	checkoutHost: 'global',
	pendingCartId: null,
	showModalCancelPendingOrder: false,
	showRecommendedPlanDrawer: false,
};

export const plansUISlice = createSlice({
	name: 'plansUI',
	initialState,
	reducers: {
		setShowModalCheckout: (state, action: PayloadAction<boolean>) => {
			state.showModalCheckout = action.payload;
			// Every visibility toggle resets ownership to the default host: openers
			// that don't declare one (sidebar, plans page, deeplink) get the global
			// modal, and a stale 'onboarding' host can never strand a later global
			// open. Onboarding openers claim 'onboarding' by dispatching
			// `setCheckoutHost` AFTER this action.
			state.checkoutHost = 'global';
		},
		// El `origin` es OBLIGATORIO a propósito: una superficie nueva que abra el
		// tour no compila hasta declarar desde dónde lo abre, así el mapa de
		// orígenes no puede quedarse atrás del producto.
		openPurchaseTour: (
			state,
			action: PayloadAction<{ cartId?: number; origin: PurchaseSuccessOrigin }>,
		) => {
			state.showPurchaseTour = true;
			state.purchaseTourCartId = action.payload.cartId ?? null;
			state.purchaseTourOrigin = action.payload.origin;
		},
		closePurchaseTour: (state) => {
			state.showPurchaseTour = false;
			state.purchaseTourCartId = null;
			state.purchaseTourOrigin = 'checkout';
		},
		setCheckoutHost: (state, action: PayloadAction<CheckoutHost>) => {
			state.checkoutHost = action.payload;
		},
		setPendingCartId: (state, action: PayloadAction<number | null>) => {
			state.pendingCartId = action.payload;
		},
		setShowModalCancelPendingOrder: (state, action: PayloadAction<boolean>) => {
			state.showModalCancelPendingOrder = action.payload;
		},
		setShowRecommendedPlanDrawer: (state, action: PayloadAction<boolean>) => {
			state.showRecommendedPlanDrawer = action.payload;
		},
	},
});

export const {
	setShowModalCheckout,
	openPurchaseTour,
	closePurchaseTour,
	setCheckoutHost,
	setPendingCartId,
	setShowModalCancelPendingOrder,
	setShowRecommendedPlanDrawer,
} = plansUISlice.actions;

export default plansUISlice.reducer;
